"use client";
import { useEffect } from "react";
import { useAtom } from "jotai";
import { marketingContextAtom } from "./marketingContextAtom";
import { useGetUserLocation } from "@/api/AppApi/services/cart";
import { CookieKeys } from "@/constants/cookiesEnums";
import { browserCookieAttrsString } from "@/utils/functions/storefront-cookie-options";

/**
 * Marketing/UTM attribution, aligned with the industry-standard model used by
 * Shopify (see CustomerJourneySummary.daysToConversion in their Admin API)
 * and GA4's default attribution lookback:
 *
 * - Stored in COOKIES, not localStorage, so the backend can read attribution
 *   at checkout/order-creation time without any extra JS forwarding.
 * - First-touch and last-touch are tracked SEPARATELY. First-touch is
 *   write-once per 30-day attribution window; last-touch is overwritten only
 *   when a new touchpoint (a URL carrying UTM/click-id params) is observed.
 * - A short-lived, rolling session id (30 min, mirrors Shopify's
 *   `_shopify_s`) is tracked separately from the 30-day attribution window —
 *   a "session" and a "campaign attribution window" are not the same thing.
 * - Call `resetMarketingAttribution()` after a successful order so the next
 *   visit starts a fresh 30-day attribution cycle, matching Shopify's
 *   per-order reset ("the first session since the last order").
 */

const SESSION_COOKIE = "mkt_session_id";
const SESSION_START_COOKIE = "mkt_session_start";
const SESSION_MAX_AGE = 30 * 60; // 30 minutes, rolling — mirrors Shopify's _shopify_s

const FIRST_TOUCH_COOKIE = "mkt_first_touch";
const LAST_TOUCH_COOKIE = "mkt_last_touch";
// 30 days: Shopify's own order-attribution window (CustomerJourneySummary)
// and GA4's default attribution lookback — the industry standard, not a guess.
const ATTRIBUTION_MAX_AGE = 30 * 24 * 60 * 60;

// TikTok's click id is read as a STANDALONE `ttclid` cookie by every
// server-side Events API call site (checkout, checkout/result, cart mutations
// — see getTtclidCookie), but nothing ever wrote it: TikTok's own SDK only sets
// `_ttp`, and the value below was previously buried inside the mkt_*_touch JSON
// blobs. Result: every TikTok server-side event went out with ttclid=undefined
// and no click→conversion attribution. 90 days mirrors the `_fbc` fallback in
// react-facebook-pixel.ts (captureFbclidAsFbc).
const CLICK_ID_MAX_AGE = 90 * 24 * 60 * 60;

interface AttributionTouch {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  ttclid: string | null;
  twclid: string | null;
  referrer: string;
  landing_page: string;
  captured_at: number;
}

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string, maxAgeSeconds: number) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/${browserCookieAttrsString()}`;
};

const removeCookie = (name: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; max-age=0; path=/${browserCookieAttrsString()}`;
};

const readTouch = (name: string): AttributionTouch | null => {
  const raw = readCookie(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AttributionTouch;
  } catch {
    return null;
  }
};

const writeTouch = (name: string, touch: AttributionTouch) => {
  writeCookie(name, JSON.stringify(touch), ATTRIBUTION_MAX_AGE);
};

/**
 * Clears first/last-touch attribution. Call this after a successful order so
 * the next visit starts a fresh 30-day attribution cycle, matching Shopify's
 * per-order reset.
 */
export function resetMarketingAttribution() {
  removeCookie(FIRST_TOUCH_COOKIE);
  removeCookie(LAST_TOUCH_COOKIE);
}

/**
 * Reads the original first-touch attribution, if any. Useful for sending
 * alongside last-touch data at checkout — order attribution typically wants
 * both, the same way Shopify exposes firstVisit and lastVisit separately.
 */
export function getFirstTouch(): AttributionTouch | null {
  return readTouch(FIRST_TOUCH_COOKIE);
}

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return "Mobile";
  if (/Tablet|iPad/i.test(ua)) return "Tablet";
  return "Desktop";
};

const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "Windows";
  if (ua.includes("Mac")) return "MacOS";
  if (ua.includes("Linux")) return "Linux";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  return "Unknown";
};

const getUtmFromUrl = (params: URLSearchParams) => ({
  utm_source: params.get("utm_source"),
  utm_medium: params.get("utm_medium"),
  utm_campaign: params.get("utm_campaign"),
  utm_term: params.get("utm_term"),
  utm_content: params.get("utm_content"),
  gclid: params.get("gclid"),
  fbclid: params.get("fbclid"),
  ttclid: params.get("ttclid"),
  twclid: params.get("twclid"),
});

const hasAttributionParams = (utm: ReturnType<typeof getUtmFromUrl>) =>
  Object.values(utm).some((value) => !!value);

export default function useInitMarketingContext() {
  const [ctx, setCtx] = useAtom(marketingContextAtom);
  const { data: ipLoc } = useGetUserLocation({ enabled: !ctx.location });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = getUtmFromUrl(params);
    const url = new URL(window.location.href);
    const landing_page = url.origin + url.pathname;
    const referrer = document.referrer || "direct";

    // Persist TikTok's click id as its own cookie so the server-side Events API
    // calls can read it. Written on every visit that carries one, so the most
    // recent ad click wins — matching TikTok's last-click attribution.
    if (utm.ttclid)
      writeCookie(CookieKeys.ttclid, utm.ttclid, CLICK_ID_MAX_AGE);

    // Rolling 30-minute session id — a "session" restarts after inactivity,
    // independent of the 30-day campaign attribution window below.
    let session_id = readCookie(SESSION_COOKIE);
    if (!session_id) {
      session_id =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    }
    writeCookie(SESSION_COOKIE, session_id, SESSION_MAX_AGE); // renew the sliding window every visit

    const existingSessionStart = readCookie(SESSION_START_COOKIE);
    const session_start = existingSessionStart ? Number(existingSessionStart) : Date.now();
    writeCookie(SESSION_START_COOKIE, String(session_start), SESSION_MAX_AGE); // keep in lockstep with session_id

    // First touch: write once, left alone until it naturally expires (30
    // days) or resetMarketingAttribution() is called after an order.
    let firstTouch = readTouch(FIRST_TOUCH_COOKIE);
    if (!firstTouch) {
      firstTouch = { ...utm, referrer, landing_page, captured_at: Date.now() };
      writeTouch(FIRST_TOUCH_COOKIE, firstTouch);
    }

    // Last touch: only overwritten when the current URL actually carries a
    // new touchpoint (UTM/click-id params) — a plain page view mid-session
    // doesn't count as a new touch. Defaults to first touch until a second,
    // distinct touchpoint occurs.
    let lastTouch = readTouch(LAST_TOUCH_COOKIE);
    if (hasAttributionParams(utm)) {
      lastTouch = { ...utm, referrer, landing_page, captured_at: Date.now() };
      writeTouch(LAST_TOUCH_COOKIE, lastTouch);
    } else if (!lastTouch) {
      lastTouch = firstTouch;
      writeTouch(LAST_TOUCH_COOKIE, lastTouch); // explicitly persist the fallback
    }

    setCtx((prev) => ({
      ...prev,
      utm_source: lastTouch.utm_source,
      utm_medium: lastTouch.utm_medium,
      utm_campaign: lastTouch.utm_campaign,
      utm_term: lastTouch.utm_term,
      utm_content: lastTouch.utm_content,
      gclid: lastTouch.gclid,
      fbclid: lastTouch.fbclid,
      ttclid: lastTouch.ttclid,
      twclid: lastTouch.twclid,
      referrer: lastTouch.referrer,
      landing_page: lastTouch.landing_page,
      session_id,
      session_start,
      session_start_iso: new Date(session_start).toISOString(),
      // Device/browser/screen/timezone are cheap and always current — refresh
      // on every mount instead of freezing them alongside sticky attribution.
      device_type: getDeviceType(),
      browser_ua: navigator.userAgent,
      os: getOS(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));
  }, [setCtx]);

  useEffect(() => {
    if (ipLoc) {
      setCtx((prev) => {
        if (prev.location) return prev; // location already set
        return {
          ...prev,
          location: {
            country: ipLoc.country_name ?? ipLoc.country,
            city: ipLoc.city ?? null,
            lat: typeof ipLoc.latitude === "number" ? ipLoc.latitude : null,
            lng: typeof ipLoc.longitude === "number" ? ipLoc.longitude : null,
          },
        };
      });
    }
  }, [ipLoc, setCtx]);
}
